'use strict';
/**
 * Shared pipeline for turning a community/marketplace GLB tank model into a
 * gallery concept: load, classify its part hierarchy by name (hull / turret /
 * wheels / rear accessory), convert whatever materials it shipped with into
 * the matte-toy-plastic look the hand-built concepts use, tint the body
 * green, normalize scale/orientation so every import sits the same way the
 * hand-built concepts do, and bolt on a pair of googly eyes that squint shut
 * on every autonomous "shot" - the same recoil state machine every other
 * concept file hand-rolls.
 *
 * Used by tank-concepts/tank-07..16-*.js - each of those is just a config
 * object (model url + a handful of tuning numbers) handed to buildGLTFTank().
 *
 * Skin-prep note: hand-built concepts group *meshes* into hullGroup/
 * wheelGroup/rear because every mesh is authored here and can be cleanly
 * bucketed. Imported multi-material meshes don't afford that - a single
 * mesh can carry both a "hull" and a "hatch" material - so imported concepts
 * key a future skin off classified *materials* instead (see
 * `entry.skinMaterials` returned below). Both are valid "skin regions", the
 * grouping axis is just whichever the source geometry actually supports.
 */
import * as THREE from 'three';
import { GLTFLoader } from '../vendor/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from '../vendor/utils/SkeletonUtils.js';
import { buildFlagTexture } from './flags.js';

const loader = new GLTFLoader();
const gltfCache = new Map();

function loadGLTF(url) {
  const key = String(url);
  if (!gltfCache.has(key)) {
    gltfCache.set(key, new Promise((resolve, reject) => {
      loader.load(key, resolve, undefined, reject);
    }));
  }
  return gltfCache.get(key);
}

// ---------------------------------------------------------------------------
// Part classification - purely name-based, since these files come from a
// dozen different unrelated hobbyist/marketplace exports with no shared
// convention. Matches against node names AND their material names.
// ---------------------------------------------------------------------------
const RE_WHEEL = /wheel|tread|track|roda|belt|tire|idler|gear/i;
const RE_TURRET = /turret|cupola|head(?!light)|kopf|neck|breech/i;
const RE_GUN = /gun|barrel|cannon|muzzle|kanone/i;
const RE_ACCESSORY = /exhaust|vent|muffler|pipe|hook|ladder|antenna|missile|chute|sweep/i;
const RE_KEEP_ORIGINAL = /light|lens|visor|lamp|glass|window|windshield/i;
// checked before RE_DARK_METAL - a lot of body-paint materials are named
// things like "Metal_Green" or "Chasis", which would otherwise get caught
// by the word "metal" and painted as bare gunmetal instead of body color.
const RE_BODY_HINT = /green|paint|colou?r|body|hull|chass?is|armou?r/i;
const RE_DARK_METAL = /metal|hatch|hinge|bolt|rivet|breech|barrel|gun|cannon|iron|thread|track|wheel|tire|belt/i;

function classifyName(name) {
  if (!name) return null;
  if (RE_WHEEL.test(name)) return 'wheel';
  if (RE_TURRET.test(name)) return 'turret';
  if (RE_GUN.test(name)) return 'gun';
  if (RE_ACCESSORY.test(name)) return 'accessory';
  return null;
}

/**
 * Walks the tree once. For every node, if its own name (or its mesh's name)
 * matches a category, the whole subtree is tagged with that category and we
 * stop descending for classification purposes (but still traverse children
 * to collect meshes). Anything never tagged defaults to 'hull'. Returns:
 *  - groups: { hull:[], turret:[], gun:[], wheel:[], accessory:[] } (nodes)
 *  - turretPivot: the single highest node tagged 'turret' (or null)
 */
function classifyHierarchy(root) {
  const groups = { hull: [], turret: [], gun: [], wheel: [], accessory: [] };
  let turretPivot = null;

  function walk(node, inherited) {
    const ownName = classifyName(node.name);
    const cat = ownName || inherited;
    if (ownName === 'turret' && !turretPivot) turretPivot = node;
    if (node.isMesh) groups[cat || 'hull'].push(node);
    node.children.forEach((child) => walk(child, cat));
  }
  root.children.forEach((child) => walk(child, null));
  return { groups, turretPivot };
}

// ---------------------------------------------------------------------------
// Materials - replace with matte MeshStandardMaterial, tinted per category.
// ---------------------------------------------------------------------------
// matches --accent-green in style.css, so imported tanks read as the same
// "family" as the site's own UI chrome (play button, etc) instead of an
// arbitrary unrelated green.
const ACCENT_GREEN = 0x36f62d;
const DARK_METAL = 0x22262b;

function convertMaterials(groups) {
  const converted = new Map(); // original material -> new material
  const skinMaterials = { body: new Set(), dark: new Set(), original: new Set() };

  function convertMesh(node, category) {
    const src = node.material;
    const list = Array.isArray(src) ? src : [src];
    const next = list.map((m) => {
      if (!m) return m;
      if (converted.has(m)) return converted.get(m);
      const name = (m.name || '') + ' ' + node.name;
      let color = ACCENT_GREEN;
      let bucket = 'body';
      if (RE_KEEP_ORIGINAL.test(name)) {
        bucket = 'original';
      } else if (category === 'wheel') {
        color = DARK_METAL;
        bucket = 'dark';
      } else if (RE_BODY_HINT.test(name)) {
        bucket = 'body';
      } else if (RE_DARK_METAL.test(name)) {
        color = DARK_METAL;
        bucket = 'dark';
      }
      // textures fight the retint: multiplying a red-painted source photo by
      // a green tint can only ever darken it (result channel is capped by
      // whatever the texture's own channel already had), never turn it
      // green - so tinted buckets drop maps entirely and go flat matte,
      // matching how every hand-built concept is already done. Only the
      // 'original' bucket (glass/lenses/lights) keeps its source look.
      const opts = {
        roughness: bucket === 'dark' ? 0.85 : 0.78,
        metalness: bucket === 'dark' ? 0.15 : 0.04,
        flatShading: false,
      };
      if (bucket !== 'original') {
        opts.color = new THREE.Color(color);
      } else {
        opts.map = m.map || null;
        opts.normalMap = m.normalMap || null;
        opts.color = m.color ? m.color.clone() : new THREE.Color(0xffffff);
      }
      const next = new THREE.MeshStandardMaterial(opts);
      converted.set(m, next);
      skinMaterials[bucket].add(next);
      return next;
    });
    node.material = Array.isArray(src) ? next : next[0];
  }

  Object.entries(groups).forEach(([category, nodes]) => {
    nodes.forEach((node) => convertMesh(node, category));
  });

  return skinMaterials;
}

// ---------------------------------------------------------------------------
// Orientation + scale normalization. Every hand-built concept faces +Z
// (barrel/eyes point +Z, gallery camera looks in from the +Z-ish octant) and
// has a footprint of roughly 2 world units. Imported models arrive in
// whatever axis/scale their exporter used, so:
//  1. find a "forward" signal (visor > gun tip > turret offset > longest
//     horizontal axis) and rotate the whole model around Y so that signal
//     points to +Z.
//  2. uniformly scale so the horizontal footprint matches the hand-built
//     concepts, then recenter on X/Z and drop to sit on y=0.
// ---------------------------------------------------------------------------
function findForwardLocal(root, groups, hullCenter) {
  const visorNode = groups.hull.find((n) => /visor|window|windshield/i.test(n.name));
  const probe = visorNode
    || groups.gun[0]
    || groups.turret[0];
  if (!probe) return null;
  const p = new THREE.Vector3();
  probe.getWorldPosition(p);
  const dir = p.clone().sub(hullCenter);
  dir.y = 0;
  if (dir.lengthSq() < 1e-6) return null;
  return dir.normalize();
}

function normalizeTransform(modelRoot, groups) {
  modelRoot.updateMatrixWorld(true);
  let box = new THREE.Box3().setFromObject(modelRoot);
  const center = box.getCenter(new THREE.Vector3());

  const fwd = findForwardLocal(modelRoot, groups, center);
  if (fwd) {
    const angle = Math.atan2(fwd.x, fwd.z);
    modelRoot.rotation.y -= angle;
  } else {
    // no semantic signal - just make sure the model isn't lying sideways
    const size = box.getSize(new THREE.Vector3());
    if (size.x > size.z) modelRoot.rotation.y = Math.PI / 2;
  }
  modelRoot.updateMatrixWorld(true);

  box = new THREE.Box3().setFromObject(modelRoot);
  const size = box.getSize(new THREE.Vector3());
  const footprint = Math.max(size.x, size.z, 0.001);
  const scale = 2.15 / footprint;
  modelRoot.scale.multiplyScalar(scale);
  modelRoot.updateMatrixWorld(true);

  box = new THREE.Box3().setFromObject(modelRoot);
  const c2 = box.getCenter(new THREE.Vector3());
  modelRoot.position.x -= c2.x;
  modelRoot.position.z -= c2.z;
  modelRoot.position.y -= box.min.y;
  modelRoot.updateMatrixWorld(true);

  return new THREE.Box3().setFromObject(modelRoot);
}

// ---------------------------------------------------------------------------
// Googly eyes - same look/behavior as the hand-built concepts: glossy white
// sclera + glossy dark pupil, periodic blink, gaze locked to the turret's
// current yaw (so the eyes always look the way the barrel points), and a
// hard squint (mounts pulled together + squashed) synced to the recoil
// "kick" phase so shooting reads as a wince.
// ---------------------------------------------------------------------------
function buildEyeRig(frontWidth, eyeSepX, eyeY, eyeZ) {
  const eyeWhiteMat = new THREE.MeshPhysicalMaterial({
    color: 0xfbfbf6, roughness: 0.35, metalness: 0, clearcoat: 0.55, clearcoatRoughness: 0.25,
  });
  const eyeBlackMat = new THREE.MeshPhysicalMaterial({
    color: 0x14120f, roughness: 0.2, metalness: 0, clearcoat: 0.7, clearcoatRoughness: 0.15,
  });

  const group = new THREE.Group();
  const mounts = { left: null, right: null };
  const pupils = { left: null, right: null };
  const baseX = { left: -eyeSepX, right: eyeSepX };
  // sized off the hull's own front-face width, not the tank's overall
  // (length-dominated) footprint - using the footprint here was the bug
  // that made every import's eyes balloon past the hull and overlap.
  const scleraR = 0.1 * frontWidth;
  const pupilR = 0.048 * frontWidth;
  const pupilRestZ = scleraR * 0.72;

  [-1, 1].forEach((side) => {
    const key = side < 0 ? 'left' : 'right';
    const mount = new THREE.Group();
    mount.position.set(side * eyeSepX, eyeY, eyeZ);
    const white = new THREE.Mesh(new THREE.SphereGeometry(scleraR, 16, 12), eyeWhiteMat);
    white.name = `eye-${key}`;
    mount.add(white);
    const pupil = new THREE.Mesh(new THREE.SphereGeometry(pupilR, 12, 10), eyeBlackMat);
    pupil.name = `eye-${key}-pupil`;
    pupil.position.set(0, 0, pupilRestZ);
    mount.add(pupil);
    group.add(mount);
    mounts[key] = mount;
    pupils[key] = pupil;
  });

  let blinkT = 0;
  let nextBlink = 1.2 + Math.random() * 2.2;
  let squint = 0;

  function update(dt, gazeYaw, recoilPhase) {
    const squintTarget = recoilPhase === 'idle' ? 0 : 1;
    squint += (squintTarget - squint) * Math.min(dt * (recoilPhase === 'kick' ? 16 : 6), 1);

    if (blinkT > 0) {
      blinkT -= dt;
    } else {
      nextBlink -= dt;
      if (nextBlink <= 0) { blinkT = 0.12; nextBlink = 2 + Math.random() * 3; }
    }
    const blinkClamped = blinkT > 0 ? Math.abs((blinkT / 0.12) - 0.5) * 2 : 1;

    // sin() saturates the pupil at its max lateral offset as the turret
    // swings past ~90 degrees, instead of needing a separate clamp - and
    // it's 0 (dead ahead) exactly when the barrel is, so no rest-pose bias.
    const px = Math.sin(gazeYaw) * 0.05 * frontWidth;

    ['left', 'right'].forEach((key) => {
      const mount = mounts[key];
      // squint is a squash only - the mount stays put at its mounted spot on
      // the hull; pulling it toward center as well as scaling it down read as
      // the eye detaching and drifting rather than squinting in place.
      mount.scale.set(1 - squint * 0.35, blinkClamped * (1 - squint * 0.45), 1 - squint * 0.15);
      pupils[key].position.set(px, 0, pupilRestZ);
    });
  }

  return { group, mounts, pupils, update };
}

// ---------------------------------------------------------------------------
// Muzzle flash - a small cone + point light at the tip of the classified gun
// mesh, oriented outward along whichever local axis that mesh's geometry is
// longest on (works for any barrel orientation/model without per-model
// tuning, the same way buildWheelPivots doesn't care which way a wheel was
// authored). `referencePoint` (world-space) is used only to tell the two
// ends of the barrel apart - whichever end sits farther from it is the
// muzzle. Returns null if nothing was classified as a gun.
// ---------------------------------------------------------------------------
function buildMuzzleFlash(groups, referencePoint) {
  const gunMesh = groups.gun[0];
  if (!gunMesh) return null;

  gunMesh.geometry.computeBoundingBox();
  const bb = gunMesh.geometry.boundingBox;
  const size = bb.getSize(new THREE.Vector3());
  const longAxis = size.x >= size.y && size.x >= size.z ? 'x' : (size.y >= size.z ? 'y' : 'z');

  const mid = bb.getCenter(new THREE.Vector3());
  const endA = mid.clone();
  const endB = mid.clone();
  endA[longAxis] = bb.min[longAxis];
  endB[longAxis] = bb.max[longAxis];

  gunMesh.updateMatrixWorld(true);
  const worldA = endA.clone().applyMatrix4(gunMesh.matrixWorld);
  const worldB = endB.clone().applyMatrix4(gunMesh.matrixWorld);
  const muzzleIsA = worldA.distanceTo(referencePoint) > worldB.distanceTo(referencePoint);
  const muzzleLocalEnd = muzzleIsA ? endA : endB;
  const muzzleWorld = muzzleIsA ? worldA : worldB;
  const breechWorld = muzzleIsA ? worldB : worldA;

  const parent = gunMesh.parent;
  const parentWorldQuat = new THREE.Quaternion();
  parent.getWorldQuaternion(parentWorldQuat);
  const outwardLocal = muzzleWorld.clone().sub(breechWorld)
    .applyQuaternion(parentWorldQuat.invert()).normalize();
  gunMesh.updateMatrix();
  const muzzleLocalToParent = muzzleLocalEnd.clone().applyMatrix4(gunMesh.matrix);

  const group = new THREE.Group();
  group.position.copy(muzzleLocalToParent);
  group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), outwardLocal);
  parent.add(group);

  // sized off the barrel's bore (its cross-section, perpendicular to
  // longAxis) rather than its own full length - a "small spark", not
  // something comparable in size to the barrel itself. Also keeps the
  // flash's own geometry from meaningfully padding out the tank's bounding
  // box beyond what the bare barrel already needed.
  const crossA = longAxis === 'x' ? size.y : size.x;
  const crossB = longAxis === 'z' ? size.y : size.z;
  const flashSize = Math.max(crossA, crossB) * 1.4;
  const flashMat = new THREE.MeshBasicMaterial({
    color: 0xfff2a8, transparent: true, opacity: 0, depthWrite: false,
  });
  const cone = new THREE.Mesh(new THREE.ConeGeometry(flashSize * 0.5, flashSize, 8), flashMat);
  cone.name = 'muzzle-flash';
  cone.position.y = flashSize * 0.35;
  group.add(cone);

  const light = new THREE.PointLight(0xffdf8a, 0, flashSize * 9);
  group.add(light);

  return { cone, light, gunMesh, longAxis };
}

// ---------------------------------------------------------------------------
// Wheel pivots (driveMode only) - imported wheel meshes are exported with
// their own local origin wherever the source tool happened to put it, not
// necessarily at the wheel's hub, so naively animating their `.rotation`
// makes them orbit/wobble instead of spin in place. Fix: group each
// wheel-classified mesh by its parent node (meshes belonging to the same
// wheel - e.g. a separate tire + rim mesh - always share one), compute that
// group's true local bounding-box center, insert a pivot Group at that
// center, and re-parent the meshes under it with a compensating offset so
// their rendered position is unchanged. Rotating the pivot then spins the
// wheel cleanly around its own hub.
// ---------------------------------------------------------------------------
function buildWheelPivots(groups) {
  const byParent = new Map();
  groups.wheel.forEach((mesh) => {
    if (!mesh.parent) return;
    if (!byParent.has(mesh.parent)) byParent.set(mesh.parent, []);
    byParent.get(mesh.parent).push(mesh);
  });

  const pivots = [];
  byParent.forEach((meshes, parent) => {
    const localBox = new THREE.Box3();
    meshes.forEach((mesh) => {
      mesh.updateMatrix();
      mesh.geometry.computeBoundingBox();
      localBox.union(mesh.geometry.boundingBox.clone().applyMatrix4(mesh.matrix));
    });
    if (localBox.isEmpty()) return;
    const hub = localBox.getCenter(new THREE.Vector3());
    const pivot = new THREE.Group();
    pivot.position.copy(hub);
    parent.add(pivot);
    meshes.forEach((mesh) => {
      pivot.add(mesh);
      mesh.position.sub(hub);
    });
    pivots.push(pivot);
  });
  return pivots;
}

// ---------------------------------------------------------------------------
// Some accessory-classified meshes model BOTH left and right instances as
// one fused mesh (Rookie's exhaust sockets and exhaust pipes are each a
// single mesh spanning both sides, not a left/right pair) - this splits such
// a mesh into two independent meshes, one per side, so each side can be
// picked/named/hidden separately in the accessory lab / part inspector
// (tank-gallery.html) instead of a click on one pipe selecting both.
//
// Triangles are bucketed by their LOCAL-space centroid X (the two lobes of
// geometry are reliably on opposite sides of the mesh's own local X=0,
// regardless of what the eventual world transform does to that axis), then
// the two results are labeled "left"/"right" by their resulting WORLD-space
// X once back in the scene graph - robust to whatever rotation
// normalizeTransform already baked into the parent chain, unlike a naive
// local-space label would be.
// ---------------------------------------------------------------------------
function splitMeshBySide(mesh) {
  if (Array.isArray(mesh.material)) return null; // multi-material mesh - not what this was built for, leave untouched
  const src = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
  const pos = src.attributes.position;
  const norm = src.attributes.normal;
  const uv = src.attributes.uv;
  const bucketA = { position: [], normal: [], uv: [] };
  const bucketB = { position: [], normal: [], uv: [] };
  const triCount = Math.floor(pos.count / 3);
  for (let t = 0; t < triCount; t++) {
    let cx = 0;
    for (let j = 0; j < 3; j++) cx += pos.getX(t * 3 + j);
    cx /= 3;
    const bucket = cx < 0 ? bucketA : bucketB;
    for (let j = 0; j < 3; j++) {
      const k = t * 3 + j;
      bucket.position.push(pos.getX(k), pos.getY(k), pos.getZ(k));
      if (norm) bucket.normal.push(norm.getX(k), norm.getY(k), norm.getZ(k));
      if (uv) bucket.uv.push(uv.getX(k), uv.getY(k));
    }
  }

  function build(bucket) {
    if (!bucket.position.length) return null;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(bucket.position, 3));
    if (bucket.normal.length) geo.setAttribute('normal', new THREE.Float32BufferAttribute(bucket.normal, 3));
    else geo.computeVertexNormals();
    if (bucket.uv.length) geo.setAttribute('uv', new THREE.Float32BufferAttribute(bucket.uv, 2));
    const m = new THREE.Mesh(geo, mesh.material);
    m.position.copy(mesh.position);
    m.rotation.copy(mesh.rotation);
    m.scale.copy(mesh.scale);
    return m;
  }

  const meshA = build(bucketA);
  const meshB = build(bucketB);
  // Only one lobe found - this mesh isn't actually a fused left/right pair
  // (e.g. a single-instance accessory part), so leave it as-is.
  if (!meshA || !meshB) return null;

  const parent = mesh.parent;
  parent.remove(mesh);
  parent.add(meshA);
  parent.add(meshB);
  parent.updateMatrixWorld(true);

  const centerA = new THREE.Box3().setFromObject(meshA).getCenter(new THREE.Vector3());
  const centerB = new THREE.Box3().setFromObject(meshB).getCenter(new THREE.Vector3());
  const left = centerA.x <= centerB.x ? meshA : meshB;
  const right = left === meshA ? meshB : meshA;
  return { left, right };
}

// Splits every 'accessory'-classified mesh that fuses both sides together
// (see splitMeshBySide) and updates `groups.accessory` in place, so
// downstream logic (buildAccessoryMounts below, and the dark/body material
// split flag-mode uses to hide only the real pipes) keeps seeing the real
// per-side meshes instead of the two originals. Named "exhaust-pipe-*" /
// "exhaust-socket-*" (dark-bucketed material = pipe, everything else =
// socket, per convertMaterials) - accurate for every accessory-classified
// part on the one model this pipeline still needs to support (Rookie's twin
// exhaust pipes), not a generic label for arbitrary future imports.
function splitAccessoryMeshes(groups, skinMaterials) {
  const next = [];
  groups.accessory.forEach((mesh) => {
    const split = splitMeshBySide(mesh);
    if (!split) { next.push(mesh); return; }
    const isPipe = skinMaterials.dark.has(mesh.material);
    const prefix = isPipe ? 'exhaust-pipe' : 'exhaust-socket';
    split.left.name = `${prefix}-left`;
    split.right.name = `${prefix}-right`;
    next.push(split.left, split.right);
  });
  groups.accessory = next;
}

// ---------------------------------------------------------------------------
// Rear accessory (exhaust smoke / flag) - the GLB meshes classified into
// groups.accessory (exhaust pipes, on every source model checked so far) are
// used as mount points: their world transform is captured here, before
// chassis.add(modelRoot) reparents everything, so it's already expressed in
// the frame chassis will use. Exhaust stays visible and just gets a
// smoke-puff emitter bolted on; a flag accessory hides the original pipe
// mesh and mounts a pole+cloth group in its place instead.
//
// Each pipe mesh's own geometry gives two useful points, found by scanning
// its REAL vertices (not the axis-aligned bounding box - a bent L-shaped
// pipe's bbox corners land in empty space next to the actual geometry,
// since "highest" and "most-backward" are reached by different segments of
// the bend, not the same corner): `base` is the lowest vertex (where it
// meets the hull deck), `tip` is whichever real vertex, in world space, is
// simultaneously highest and furthest back (world -Z, since the whole model
// faces +Z) - the bent, business end of the pipe. Reading this off the
// actual mesh instead of a hardcoded shape works for any pipe bend.
// ---------------------------------------------------------------------------
function buildAccessoryMounts(groups) {
  return groups.accessory.map((mesh) => {
    mesh.updateMatrixWorld(true);
    const posAttr = mesh.geometry.attributes.position;
    const v = new THREE.Vector3();
    let base = null;
    let tip = null;
    let lowestY = Infinity;
    let bestTipScore = -Infinity;
    for (let i = 0; i < posAttr.count; i++) {
      v.fromBufferAttribute(posAttr, i).applyMatrix4(mesh.matrixWorld);
      if (v.y < lowestY) { lowestY = v.y; base = v.clone(); }
      const tipScore = v.y - v.z;
      if (tipScore > bestTipScore) { bestTipScore = tipScore; tip = v.clone(); }
    }
    if (!base) base = new THREE.Vector3().setFromMatrixPosition(mesh.matrixWorld);
    if (!tip) tip = base.clone();
    return { mesh, base, tip };
  });
}

let smokeTexture = null;
function getSmokeTexture() {
  if (smokeTexture) return smokeTexture;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,0.9)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.4)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  smokeTexture = new THREE.CanvasTexture(c);
  return smokeTexture;
}

// Smoke needs to read as "haze" against whatever's behind it: light/warm
// gray shows up against the dark game canvas and the mascot's dark start
// screen, but turns near-invisible (and looks like a rendering glitch, not
// smoke) against a light background - so it flips to near-black whenever the
// page is in light mode. Only index.html's theme toggle (client.js,
// `document.documentElement.setAttribute('data-theme', ...)`) ever sets this
// attribute; standalone pages (tank-gallery.html, tank-studio.html) never do,
// so they always read as dark/no-attribute and get the light-gray smoke.
function getSmokePuffColor() {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 0x000000 : 0xd9dbd3;
}

// Small reused pool of billboard sprites per mount point, each drifting up,
// growing and fading on its own staggered loop - cheap enough to run on
// every GLB tank by default (no caller changes needed for this to show up
// everywhere `buildGLTFTank` is already used).
function buildExhaustSmoke(mounts, namePrefix) {
  const group = new THREE.Group();
  const puffsPerMount = 4;
  const puffs = [];
  let lastColor = getSmokePuffColor();

  mounts.forEach((mount) => {
    const anchor = new THREE.Group();
    anchor.position.copy(mount.tip); // the bent-back opening, not the pipe's hull-level base
    group.add(anchor);
    const baseSize = Math.max(mount.tip.distanceTo(mount.base), 0.05) * 0.55;
    for (let i = 0; i < puffsPerMount; i++) {
      const mat = new THREE.SpriteMaterial({
        map: getSmokeTexture(), color: lastColor, transparent: true, depthWrite: false, opacity: 0,
      });
      const sprite = new THREE.Sprite(mat);
      sprite.name = namePrefix || 'accessory-smoke';
      // a fresh Sprite defaults to a 1x1-unit quad until update() below first
      // runs and rescales it - harmless at the original tiny baseSize, but
      // with a calibrated accessory group scaled up (see
      // ROOKIE_ACCESSORY_LAYOUT's smoke1/smoke2), that one still-frame default
      // is huge and inflates any bounding-box-based camera auto-fit
      // (tank-gallery.js's setupViewport) into zooming the whole tank out to
      // a speck before the first real frame ever renders.
      sprite.scale.setScalar(baseSize);
      anchor.add(sprite);
      puffs.push({
        sprite,
        baseSize,
        age: (i / puffsPerMount) * 1.4,
        life: 1.1 + Math.random() * 0.5,
        driftX: (Math.random() - 0.5) * baseSize * 0.6,
        driftZ: (Math.random() - 0.5) * baseSize * 0.6,
      });
    }
  });

  function update(dt) {
    // cheap per-frame check (one DOM attribute read + string compare) so a
    // live theme toggle (mascot still on screen) recolors the smoke
    // immediately instead of only on the next page load.
    const color = getSmokePuffColor();
    if (color !== lastColor) {
      lastColor = color;
      puffs.forEach((p) => p.sprite.material.color.setHex(color));
    }
    puffs.forEach((p) => {
      p.age += dt;
      if (p.age >= p.life) {
        p.age -= p.life;
        p.life = 1.1 + Math.random() * 0.5;
        p.driftX = (Math.random() - 0.5) * p.baseSize * 0.6;
        p.driftZ = (Math.random() - 0.5) * p.baseSize * 0.6;
      }
      const frac = p.age / p.life;
      p.sprite.position.set(p.driftX * frac, frac * p.baseSize * 2.2, p.driftZ * frac);
      p.sprite.scale.setScalar(p.baseSize * (0.35 + frac * 0.9));
      p.sprite.material.opacity = Math.sin(Math.min(frac, 1) * Math.PI) * 0.5;
    });
  }

  return { group, update };
}

// Pole + cloth-like flag at each mount point. The pole leans along the real
// pipe's own base->tip bend (read off the mesh by buildAccessoryMounts, not
// a hardcoded shape), and the flag fabric extends backward off the tip -
// continuing that same bend - instead of out to the side. The cloth
// "physics" is a per-vertex sine wave whose amplitude grows from 0 at the
// hoist (pole) edge to full amplitude at the free edge, driven by an
// always-on idle flutter plus a wind term the caller feeds in from the
// tank's own drive/turn state (see tick() below) - so it reads as reacting
// to the tank moving/turning/stopping instead of just looping in place.
function buildFlagAccessory(mounts, countryId) {
  const group = new THREE.Group();
  const poleMat = new THREE.MeshStandardMaterial({ color: 0x22262b, roughness: 0.7, metalness: 0.1 });
  const { texture, aspect } = buildFlagTexture(countryId);
  const flagMat = new THREE.MeshStandardMaterial({
    map: texture, roughness: 0.85, metalness: 0, side: THREE.DoubleSide,
  });

  // both flags render at the same size regardless of any per-pipe modeling
  // asymmetry in the source mesh - shared reach = the average of every
  // mount's own base->tip distance.
  const reach = mounts.reduce((s, m) => s + m.base.distanceTo(m.tip), 0) / mounts.length;
  const poleHeight = Math.max(reach, 0.05) * 1.05;

  const flags = [];
  mounts.forEach((mount, idx) => {
    const up = mount.tip.clone().sub(mount.base);
    if (up.lengthSq() < 1e-6) up.set(0, 1, 0); else up.normalize();
    // desired flag-extend direction is "backward" (world -Z, since the
    // model faces +Z), projected perpendicular to the pole's own lean so it
    // stays a valid basis vector even when the pipe bends sharply.
    let flagDir = new THREE.Vector3(0, 0, -1).sub(up.clone().multiplyScalar(up.dot(new THREE.Vector3(0, 0, -1))));
    if (flagDir.lengthSq() < 1e-6) flagDir = new THREE.Vector3(1, 0, 0).sub(up.clone().multiplyScalar(up.x));
    flagDir.normalize();
    const right = new THREE.Vector3().crossVectors(up, flagDir).normalize();
    flagDir.crossVectors(right, up).normalize(); // re-orthogonalize

    const poleGroup = new THREE.Group();
    poleGroup.position.copy(mount.base);
    poleGroup.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(flagDir, up, right));
    group.add(poleGroup);

    const flagH = poleHeight * 0.62;
    const flagW = flagH * aspect;
    const flagCenterY = poleHeight * 0.86;
    // the flag's own top edge sits at flagCenterY + flagH/2, which is above
    // poleHeight (where the plain cylinder used to end) - stretch the
    // visible pole so it actually reaches the top of the cloth, plus a
    // small finial poking past it like a real flagpole, instead of the
    // cloth's top corner floating above where the pole stops.
    const poleVisualHeight = flagCenterY + flagH / 2 + poleHeight * 0.06;
    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(poleHeight * 0.03, poleHeight * 0.035, poleVisualHeight, 8),
      poleMat,
    );
    pole.position.y = poleVisualHeight / 2;
    poleGroup.add(pole);

    const geo = new THREE.PlaneGeometry(flagW, flagH, 8, 6);
    geo.translate(flagW / 2, 0, 0); // x=0 is the hoist edge, attached at the pole
    const mesh = new THREE.Mesh(geo, flagMat);
    mesh.position.set(0, flagCenterY, 0);
    // local +X (the flag's extend direction) is basis vector `flagDir`
    // (backward) thanks to the poleGroup rotation above, not world X.
    poleGroup.add(mesh);

    flags.push({ geo, flagW, flagH, side: idx === 0 ? -1 : 1 });
  });

  let t = 0;
  function update(dt, wind) {
    t += dt;
    const windAmp = wind ? wind.windAmp : 0;
    const windSpeed = wind ? wind.windSpeed : 0;
    const snap = wind ? wind.snap : 0;
    flags.forEach((f) => {
      const pos = f.geo.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const frac = x / f.flagW;
        const rowPhase = y / f.flagH; // per-row offset so rows don't flap in lockstep, reads as cloth instead of a rigid plank
        const idleAmp = f.flagH * 0.12;
        const amp = (idleAmp + f.flagH * 0.28 * windAmp) * frac;
        const z = Math.sin(t * (2.6 + windSpeed * 3.6) - x * 6 + rowPhase * 1.6) * amp
          + Math.sin(t * (5.3 + windSpeed * 5) - x * 11 + rowPhase * 2.4) * amp * 0.35
          + Math.sin(t * 1.3 + f.side) * idleAmp * 0.4 * frac
          + snap * f.flagH * 0.32 * frac;
        pos.setZ(i, z);
      }
      pos.needsUpdate = true;
      f.geo.computeVertexNormals();
    });
  }

  return { group, update };
}

// Calibrated by hand in the accessory lab (tank-gallery.js's LAB_MODE,
// cfg.accessory === 'debug') against the Rookie GLB
// (public/models/low-poly.glb) - vertex-scanning the real exhaust-pipe
// meshes for a mount point (buildAccessoryMounts, above) kept producing
// wrong placements across the pipes' bent geometry: flags clustered
// together and pointing sideways, smoke rising from the wrong point. These
// are fixed, hand-placed chassis-local transforms instead (position, Euler
// rotation in degrees, scale), passed in by tank-08-rookie.js /
// tank-17-rookie-flag.js as cfg.accessoryLayout. Only valid for this
// specific GLB - every other imported concept has no accessoryLayout and
// keeps falling back to the vertex-scan mounts.
export const ROOKIE_ACCESSORY_LAYOUT = {
  flag1: { pos: { x: -0.26, y: 0.495, z: -0.85 }, rot: { x: -10, y: 10.5, z: 0 }, scale: { x: 1, y: 1.85, z: 1.85 } },
  flag2: { pos: { x: 0.26, y: 0.495, z: -0.85 }, rot: { x: -10, y: -10.5, z: 0 }, scale: { x: 1, y: 1.85, z: 1.85 } },
  smoke1: { pos: { x: -0.295, y: 0.96, z: -0.97 }, rot: { x: -90, y: 0, z: 10.5 }, scale: { x: 8, y: 8, z: 8 } },
  smoke2: { pos: { x: 0.295, y: 0.96, z: -0.97 }, rot: { x: -90, y: 0, z: -10.5 }, scale: { x: 8, y: 8, z: 8 } },
};

function applyLabTransform(group, t) {
  group.position.set(t.pos.x, t.pos.y, t.pos.z);
  group.rotation.set(
    THREE.MathUtils.degToRad(t.rot.x),
    THREE.MathUtils.degToRad(t.rot.y),
    THREE.MathUtils.degToRad(t.rot.z),
  );
  if (t.scale) group.scale.set(t.scale.x, t.scale.y, t.scale.z);
}

// Anchors each flag group (flag1/flag2) EXACTLY on the real
// exhaust-socket-left/right mesh's own position/rotation (matched to
// whichever flag group sits closest to it, using its layout-guessed
// position purely as a left/right hint: a plain distance compare in
// chassis-local space, which - since chassis has no rotation of its own at
// build time - is the same frame the real sockets' world position ends up
// in, so no left/right sign convention to get backwards). This overwrites
// whatever position/rotation ROOKIE_ACCESSORY_LAYOUT guessed for the flag
// with the real, already-correct exhaust mount transform - the whole reason
// this is worth doing is that the pole/cloth (built in the flag group's own
// local frame around poleHeight, unrelated to this anchor) then rise from
// exactly the same spot a real exhaust pipe would, instead of the socket
// sub-mesh alone matching while pole/cloth stay wherever the separately
// hand-calibrated layout put them - which is what floated the socket away
// from the pole in the first place. Scale is deliberately left alone (still
// whatever the layout/lab calibrated) - pole/cloth sizing doesn't depend on
// it, see poleHeight/flagH/flagW below, all hull-size-derived.
//
// The socket itself is an exact copy-paste of the real mesh's rendered
// appearance: its geometry is baked to chassis-frame WORLD coordinates via
// `source.matrixWorld` (a plain "what you see is what you get" transform -
// exactly how the real exhaust socket already renders, no decomposed
// position/rotation/scale to keep in sync with each other and get subtly
// wrong), then parented under the flag group with that group's own
// transform CANCELLED OUT (g.matrixWorld^-1), so the already-correct baked
// shape renders unmoved and undistorted regardless of whatever
// position/rotation/scale the flag group itself has. Falls back to a
// generic procedural stub, at the flag's own (unmodified, hand-calibrated)
// position, for a hypothetical GLB whose exhaust didn't classify/split
// cleanly.
function attachFlagSockets(chassis, flagGroups, exhaustSocketMeshes, poleHeight) {
  const entries = Object.entries(flagGroups);
  const sockets = [exhaustSocketMeshes && exhaustSocketMeshes.left, exhaustSocketMeshes && exhaustSocketMeshes.right].filter(Boolean);

  if (!sockets.length) {
    const socketMat = new THREE.MeshStandardMaterial({ color: ACCENT_GREEN, roughness: 0.78, metalness: 0.04 });
    entries.forEach(([key, g]) => {
      const h = poleHeight * 0.16;
      const socket = new THREE.Mesh(new THREE.CylinderGeometry(poleHeight * 0.05, poleHeight * 0.065, h, 8), socketMat);
      socket.name = `accessory-${key}-socket`;
      socket.position.y = h / 2;
      g.add(socket);
    });
    return;
  }

  chassis.updateMatrixWorld(true);
  const socketWorldPos = sockets.map((s) => new THREE.Vector3().setFromMatrixPosition(s.matrixWorld));
  const used = new Set();
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scratchScale = new THREE.Vector3();

  entries.forEach(([key, g]) => {
    let bestIdx = -1;
    let bestDist = Infinity;
    socketWorldPos.forEach((p, i) => {
      if (used.has(i)) return;
      const d = p.distanceTo(g.position); // g.position is chassis-local, same frame socketWorldPos was computed in
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    });
    if (bestIdx === -1) return;
    used.add(bestIdx);
    const source = sockets[bestIdx];

    // Anchor the whole flag group (pole+cloth+socket) at the real socket's
    // exact position/rotation, so pole/cloth (built in g's own local frame)
    // rise from exactly the same spot a real exhaust pipe would, not just
    // the socket sub-mesh alone. g's parent (chassis) has identity
    // transform at build time, so source.matrixWorld (also expressed
    // relative to that same root/chassis frame) can be copied straight
    // into g's LOCAL position/rotation with no matrix inversion needed.
    // Scale is deliberately left alone (still whatever the layout/lab
    // calibrated) - pole/cloth sizing doesn't depend on it.
    source.matrixWorld.decompose(pos, quat, scratchScale);
    g.position.copy(pos);
    g.quaternion.copy(quat);
    g.updateMatrixWorld(true);

    const geo = source.geometry.clone();
    geo.applyMatrix4(source.matrixWorld);
    const mat = Array.isArray(source.material) ? source.material[0] : source.material;
    const socket = new THREE.Mesh(geo, mat);
    socket.name = `accessory-${key}-socket`;

    const invG = new THREE.Matrix4().copy(g.matrixWorld).invert();
    const sp = new THREE.Vector3();
    const sq = new THREE.Quaternion();
    const ss = new THREE.Vector3();
    invG.decompose(sp, sq, ss);
    socket.position.copy(sp);
    socket.quaternion.copy(sq);
    socket.scale.copy(ss);

    g.add(socket);
  });
}

// Builds the flag1/flag2/smoke1/smoke2 groups from `layout` (chassis-local
// transforms, see ROOKIE_ACCESSORY_LAYOUT) when given, or - only for the
// accessory lab calibrating a not-yet-calibrated model - from a rough
// hull-relative guess (rear-ish, left/right of centerline) that a human then
// nudges into place with the lab's sliders. Used for all three of 'flag'/
// 'exhaust'/'debug' cfg.accessory modes whenever cfg.accessoryLayout is set
// (see buildGLTFTank below); which pair ends up visible, and whether the
// real GLB pipe meshes stay visible, is decided by the caller per mode.
// `exhaustSocketMeshes` (the real, already-split exhaust-socket-left/right
// meshes, see splitAccessoryMeshes) is threaded through to attachFlagSockets
// below so the flags' own sockets can reuse that real geometry.
function buildAccessoryRig(chassis, hullBox, flagCountry, layout, exhaustSocketMeshes) {
  const size = hullBox.getSize(new THREE.Vector3());
  const poleMat = new THREE.MeshStandardMaterial({ color: 0x22262b, roughness: 0.7, metalness: 0.1 });
  const { texture, aspect } = buildFlagTexture(flagCountry || 'ch');
  const flagMat = new THREE.MeshStandardMaterial({
    map: texture, roughness: 0.85, metalness: 0, side: THREE.DoubleSide,
  });

  const poleHeight = Math.max(size.y * 0.5, 0.05);
  const flagH = poleHeight * 0.62;
  const flagW = flagH * aspect;
  const clothSides = [];

  function makeFlagGroup(key, x) {
    const g = new THREE.Group();
    if (layout && layout[key]) {
      applyLabTransform(g, layout[key]);
    } else {
      g.position.set(x, hullBox.min.y + size.y * 0.55, hullBox.min.z);
    }
    chassis.add(g);

    const flagCenterY = poleHeight * 0.86;
    // see buildFlagAccessory's identical fix: stretch the visible pole to
    // reach the cloth's own top edge (flagCenterY + flagH/2), plus a small
    // finial past it, instead of ending at poleHeight while the flag's top
    // corner floats above that.
    const poleVisualHeight = flagCenterY + flagH / 2 + poleHeight * 0.06;
    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(poleHeight * 0.03, poleHeight * 0.035, poleVisualHeight, 8),
      poleMat,
    );
    pole.name = `accessory-${key}-pole`;
    pole.position.y = poleVisualHeight / 2;
    g.add(pole);

    const geo = new THREE.PlaneGeometry(flagW, flagH, 8, 6);
    geo.translate(flagW / 2, 0, 0); // x=0 is the hoist edge, attached at the pole
    const mesh = new THREE.Mesh(geo, flagMat);
    mesh.name = `accessory-${key}-cloth`;
    mesh.position.set(0, flagCenterY, 0);
    mesh.rotation.y = Math.PI / 2; // extend local +X out to local -Z (backward) by default
    g.add(mesh);

    clothSides.push({ geo, flagW, flagH, side: clothSides.length === 0 ? -1 : 1 });
    return g;
  }

  function makeSmokeGroup(key, x) {
    const g = new THREE.Group();
    if (layout && layout[key]) {
      applyLabTransform(g, layout[key]);
    } else {
      g.position.set(x, hullBox.min.y + size.y * 0.32, hullBox.min.z);
    }
    chassis.add(g);
    // buildExhaustSmoke anchors its puffs at mount.tip in the group it
    // returns; a single degenerate mount at the group's own local origin
    // (tip) with base offset just enough to size the puffs lets the group's
    // own position/rotation/scale (the thing the lab sliders drive, or the
    // calibrated layout above) do all the real placement work.
    const rig = buildExhaustSmoke([{ base: new THREE.Vector3(0, -poleHeight * 0.25, 0), tip: new THREE.Vector3(0, 0, 0) }], `accessory-${key}`);
    g.add(rig.group);
    return { group: g, update: rig.update };
  }

  const flag1 = makeFlagGroup('flag1', -size.x * 0.28);
  const flag2 = makeFlagGroup('flag2', size.x * 0.28);
  attachFlagSockets(chassis, { flag1, flag2 }, exhaustSocketMeshes, poleHeight);
  const smoke1 = makeSmokeGroup('smoke1', -size.x * 0.28);
  const smoke2 = makeSmokeGroup('smoke2', size.x * 0.28);

  let t = 0;
  function update(dt, wind) {
    t += dt;
    const windAmp = wind ? wind.windAmp : 0;
    const windSpeed = wind ? wind.windSpeed : 0;
    const snap = wind ? wind.snap : 0;
    clothSides.forEach((f) => {
      const pos = f.geo.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const frac = x / f.flagW;
        const rowPhase = y / f.flagH; // per-row offset so rows don't flap in lockstep, reads as cloth instead of a rigid plank
        const idleAmp = f.flagH * 0.12;
        const amp = (idleAmp + f.flagH * 0.28 * windAmp) * frac;
        const z = Math.sin(t * (2.6 + windSpeed * 3.6) - x * 6 + rowPhase * 1.6) * amp
          + Math.sin(t * (5.3 + windSpeed * 5) - x * 11 + rowPhase * 2.4) * amp * 0.35
          + Math.sin(t * 1.3 + f.side) * idleAmp * 0.4 * frac
          + snap * f.flagH * 0.32 * frac;
        pos.setZ(i, z);
      }
      pos.needsUpdate = true;
      f.geo.computeVertexNormals();
    });
    smoke1.update(dt);
    smoke2.update(dt);
  }

  return { flag1, flag2, smoke1: smoke1.group, smoke2: smoke2.group, update };
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------
/**
 * @param {object} cfg
 * @param {string|URL} cfg.url            GLB location
 * @param {number}    [cfg.eyeHeightFrac] 0..1, where up the hull front face
 *                                        the eyes sit (default 0.62)
 * @param {number}    [cfg.eyeSepFrac]    eye separation as a fraction of
 *                                        footprint width (default 0.16)
 * @param {boolean}   [cfg.driveMode]     swaps the idle z-bob for a real
 *                                        drive/turn state machine (root
 *                                        creeps within a clamped box and
 *                                        pivot-turns, sometimes far enough
 *                                        to show its back) plus spinning
 *                                        wheels - for the one "hero" render
 *                                        of a tank, not the gallery grid.
 */
export async function buildGLTFTank(cfg) {
  const gltf = await loadGLTF(cfg.url);
  // loadGLTF caches the parsed result per URL so repeat instances (e.g. the
  // mascot, the studio, and more than one gallery card - see
  // tank-17-rookie-flag.js, which reuses tank-08-rookie.js's model) skip
  // re-parsing the GLB - but every caller below mutates its scene graph in
  // place (hiding parts, converting materials, reparenting into its own
  // chassis). Sharing the literal cached Object3D across instances would
  // have each new instance steal nodes out of the previous one's chassis via
  // reparenting, leaving it bodyless - so each call gets its own clone to
  // mutate.
  const modelRoot = cloneSkeleton(gltf.scene || gltf.scenes[0]);

  // some source parts are redundant once the googly eyes are on (e.g. a
  // model's own headlight fixtures right where the eyes get mounted) -
  // `cfg.hideParts` takes exact mesh/node names to switch off.
  if (cfg.hideParts) {
    modelRoot.traverse((n) => {
      if (cfg.hideParts.includes(n.name)) n.visible = false;
    });
  }

  const { groups, turretPivot } = classifyHierarchy(modelRoot);
  const skinMaterials = convertMaterials(groups);
  const box = normalizeTransform(modelRoot, groups);
  // Split any accessory mesh that fuses both left+right instances together
  // (see splitAccessoryMeshes) before anything below reads groups.accessory,
  // so mount-finding and flag-mode's pipe-hiding both see the real per-side
  // meshes.
  splitAccessoryMeshes(groups, skinMaterials);
  // Used by buildAccessoryRig (via attachFlagSockets) to give each flag its
  // own socket built from the real exhaust-socket mesh's own geometry,
  // instead of a generic procedural shape.
  const exhaustSocketMeshes = {
    left: groups.accessory.find((m) => m.name === 'exhaust-socket-left') || null,
    right: groups.accessory.find((m) => m.name === 'exhaust-socket-right') || null,
  };
  // captured now, before modelRoot is reparented into chassis below - at this
  // point modelRoot has no parent of its own, so world position == the frame
  // chassis will use once it owns modelRoot. Hoisted above the accessory
  // section (rather than only where the eyes need it further down) since the
  // debug calibration rig also needs a hull-only box to place its starting
  // guesses.
  const hullBox = new THREE.Box3();
  groups.hull.forEach((n) => hullBox.expandByObject(n));
  const accessoryMounts = buildAccessoryMounts(groups);

  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const scale = Math.max(size.x, size.z, 0.5);

  const root = new THREE.Group();
  const chassis = new THREE.Group();
  root.add(chassis);
  chassis.add(modelRoot);

  // rear accessory: 'exhaust' (default, GLB pipes stay + get smoke puffs),
  // 'flag' (pipes hidden, a pole+cloth flag mounted in their place instead),
  // or 'debug' (pipes stay visible as a reference, plus both pairs shown at
  // once for calibration - see tank-gallery.js's LAB_MODE). When the caller
  // passes cfg.accessoryLayout (currently only the Rookie GLB via
  // ROOKIE_ACCESSORY_LAYOUT), all three modes route through the single
  // calibrated buildAccessoryRig instead of the vertex-scan mounts below -
  // only which pair ends up .visible, and whether the real pipes stay
  // visible, differs per mode.
  //
  // Some source models (Rookie included) model the exhaust as two meshes
  // fused only by node name: a hull-colored socket the pipe stands in
  // ('body' skin bucket) and the dark pipe itself ('dark' bucket) - both
  // get tagged 'accessory' by RE_ACCESSORY since they share the "exhausts"
  // node name. Flag mode should only swap out the pipe, not the socket it
  // stands on, so only 'dark'-bucketed accessory meshes get hidden here
  // (falling back to hiding every accessory mesh if none is dark-bucketed,
  // e.g. a model whose pipe never picked up a metal-ish material name).
  const pipeMounts = accessoryMounts.filter((m) => skinMaterials.dark.has(m.mesh.material));
  const flagHideMounts = pipeMounts.length ? pipeMounts : accessoryMounts;
  const accessoryGroup = new THREE.Group();
  chassis.add(accessoryGroup);
  let accessoryRig = null;
  let accessoryDebugRig = null;
  if (cfg.accessoryLayout) {
    const rig = buildAccessoryRig(chassis, hullBox, cfg.flagCountry || 'ch', cfg.accessoryLayout, exhaustSocketMeshes);
    const showFlags = cfg.accessory === 'flag' || cfg.accessory === 'debug';
    const showSmoke = cfg.accessory !== 'flag';
    rig.flag1.visible = showFlags;
    rig.flag2.visible = showFlags;
    rig.smoke1.visible = showSmoke;
    rig.smoke2.visible = showSmoke;
    if (cfg.accessory === 'flag') flagHideMounts.forEach((m) => { m.mesh.visible = false; });
    accessoryDebugRig = rig;
  } else if (cfg.accessory === 'flag') {
    flagHideMounts.forEach((m) => { m.mesh.visible = false; });
    accessoryRig = buildFlagAccessory(flagHideMounts, cfg.flagCountry || 'ch');
    accessoryGroup.add(accessoryRig.group);
  } else if (cfg.accessory === 'debug') {
    accessoryDebugRig = buildAccessoryRig(chassis, hullBox, cfg.flagCountry || 'ch', null, exhaustSocketMeshes);
  } else {
    accessoryRig = buildExhaustSmoke(accessoryMounts);
    accessoryGroup.add(accessoryRig.group);
  }

  // eyes, mounted on the +Z (front) face of the hull's OWN bounding box -
  // deliberately not `box` (the whole model, turret+gun included), since the
  // gun barrel always reaches further +Z than the hull face. Anchoring off
  // the whole-model box left the eyes floating out near the barrel tip,
  // detached from the body. Falls back to the whole-model box if nothing
  // was classified as hull (shouldn't normally happen).
  const eyeBox = hullBox.isEmpty() ? box : hullBox;
  const eyeSize = eyeBox.getSize(new THREE.Vector3());

  const eyeHeightFrac = cfg.eyeHeightFrac ?? 0.62;
  const eyeSepFrac = cfg.eyeSepFrac ?? 0.16;
  // how far the eye center sits out from the hull's front face, as a
  // fraction of `scale`. Positive floats the eye proud of the surface,
  // negative sinks the center behind it so more of the sphere is embedded
  // (only a cap pokes out) - unrelated to eyeSepFrac, which only spaces the
  // two eyes apart/together along the hull's front face, not into it.
  const eyeProudFrac = cfg.eyeProudFrac ?? -0.012;
  const eyeY = eyeBox.min.y + eyeSize.y * eyeHeightFrac;
  const eyeZ = eyeBox.max.z + eyeProudFrac * scale;
  const eyeRig = buildEyeRig(eyeSize.x, eyeSize.x * eyeSepFrac, eyeY, eyeZ);
  chassis.add(eyeRig.group);

  const wheelPivots = cfg.driveMode ? buildWheelPivots(groups) : [];
  const muzzleFlash = buildMuzzleFlash(groups, center);

  // turret pivot base position (world -> chassis-local), for the autonomous
  // scan + recoil kick. If nothing classified as a turret, both are skipped
  // and the tank just gets idle root motion + eye business.
  let turretBase = null;
  let turretForwardLocal = null;
  if (turretPivot) {
    turretBase = turretPivot.position.clone();
    // approximate the turret's own "forward" as the offset from hull center
    // to the turret itself, expressed in the turret's parent's local frame -
    // good enough for a believable kickback direction without needing to
    // know the model's original rig conventions. A LOCAL-space vector like
    // this automatically stays correct as the parent chain turns (e.g.
    // driveMode's root yaw) since it's re-composed with whatever the
    // parent's current orientation is every frame - no restale-ing needed.
    const worldPos = new THREE.Vector3();
    turretPivot.getWorldPosition(worldPos);
    const parentLocalCenter = turretPivot.parent.worldToLocal(center.clone());
    const localCenterAgain = turretPivot.parent.worldToLocal(worldPos.clone());
    const dir = localCenterAgain.clone().sub(parentLocalCenter);
    dir.y = 0;
    if (dir.lengthSq() > 1e-6) turretForwardLocal = dir.normalize();
  }

  // how far/fast the turret sweeps left/right while idly "scanning", in
  // radians / angular speed. Defaults match the original fixed values;
  // models with a long barrel relative to hull width (e.g. Rookie) need a
  // smaller sweep or the barrel tip swings out past the hull's silhouette -
  // driveMode's hero render instead widens this deliberately (see
  // menu-tank.js) so the turret can swing toward the tank's own side/back,
  // not just idle-sway near dead-ahead.
  const turretScanAmplitude = cfg.turretScanAmplitude ?? 0.32;
  const turretScanSpeed = cfg.turretScanSpeed ?? 0.6;

  let t = 0;
  let recoil = 0;
  let recoilPhase = 'idle';
  let nextShot = 2.5 + Math.random() * 3.5;
  let turretYaw = 0; // current turret-vs-chassis yaw, radians - also drives eye gaze

  // ---- driveMode-only: creep forward/back and pivot-turn left/right, same
  // state-machine shape as the hand-built hero tank's animator. Turn amount
  // is deliberately wide (up to ~1.7rad) and only softly leashed back once
  // past ~40 degrees, so an unlucky run of same-direction turns can swing
  // the tank far enough to show its back before the leash bias corrects it -
  // instead of only ever creeping in place facing the camera. ----
  let moveState = 'drive';
  let moveT = 0;
  let moveDur = 1.4;
  let driveDir = 1;
  let turnDir = 1;
  let turnAmount = 0;
  let turnStart = 0;
  let wheelAngle = 0;
  let flagSnap = 0; // decaying whip-snap impulse, kicked on every drive<->turn transition
  const driveSpeed = scale * 0.11;
  const driveClampX = scale * 0.2;
  const driveClampZ = scale * 0.16;

  function pickNextMove() {
    flagSnap = 1;
    const r = Math.random();
    if (r < 0.45) {
      moveState = 'turn';
      const leash = 0.7;
      turnDir = root.rotation.y > leash ? -1 : root.rotation.y < -leash ? 1 : (Math.random() < 0.5 ? -1 : 1);
      turnAmount = (0.35 + Math.random() * 1.35) * turnDir;
      turnStart = root.rotation.y;
      moveDur = 0.8 + Math.abs(turnAmount) * 0.55;
    } else {
      moveState = 'drive';
      driveDir = Math.random() < 0.5 ? -1 : 1;
      moveDur = 1.1 + Math.random() * 1.1;
    }
    moveT = 0;
  }

  function tick(dt, pointer) {
    t += dt;

    if (cfg.static) {
      // calibration lab (accessory lab, tank-gallery.js LAB_MODE): the tank
      // must hold still so the human can pixel-match the accessory sliders
      // against a fixed reference pose - both driveMode's creep/turn and the
      // regular idle bob/sway below are skipped entirely. OrbitControls
      // (user-driven rotate/zoom) is untouched, that's camera-side, not this.
    } else if (cfg.driveMode) {
      moveT += dt;
      let wheelRate = 0;
      if (moveState === 'drive') {
        root.position.z += Math.cos(root.rotation.y) * driveDir * driveSpeed * dt;
        root.position.x += Math.sin(root.rotation.y) * driveDir * driveSpeed * dt;
        root.position.x = THREE.MathUtils.clamp(root.position.x, -driveClampX, driveClampX);
        root.position.z = THREE.MathUtils.clamp(root.position.z, -driveClampZ, driveClampZ);
        wheelRate = driveDir * 9;
      } else {
        const p = Math.min(moveT / moveDur, 1);
        const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        root.rotation.y = turnStart + turnAmount * eased;
        wheelRate = turnDir * 7;
      }
      wheelAngle += wheelRate * dt;
      wheelPivots.forEach((p) => { p.rotation.x = wheelAngle; });
      chassis.position.y = Math.abs(Math.sin(t * 6.2)) * 0.01 * scale;
      if (moveT >= moveDur) pickNextMove();
    } else {
      const cyc = t * 0.55;
      root.position.z = Math.sin(cyc) * 0.28;
      chassis.rotation.z = Math.sin(cyc * 2 + Math.PI / 2) * 0.025;
      chassis.position.y = Math.abs(Math.sin(cyc * 2)) * 0.015 * scale;
    }

    if (flagSnap > 0) flagSnap = Math.max(0, flagSnap - dt * 2.5);
    if (accessoryRig || accessoryDebugRig) {
      const windAmp = cfg.driveMode ? (moveState === 'drive' ? 1 : 0.45) : 0;
      const windSpeed = cfg.driveMode
        ? (moveState === 'drive' ? Math.abs(driveDir) : Math.min(Math.abs(turnAmount), 1.6) * 0.6)
        : 0;
      const wind = { windAmp, windSpeed, snap: flagSnap };
      if (accessoryRig) accessoryRig.update(dt, wind);
      if (accessoryDebugRig) accessoryDebugRig.update(dt, wind);
    }

    // turret scan + recoil/muzzle-flash also count as "the tank moving" for
    // calibration purposes (they swivel/kick parts even with the body itself
    // held still by cfg.static above), so they're frozen at their rest pose
    // too - only the accessory rig's own cloth/smoke animation (updated
    // above) and eye blink/gaze keep running.
    if (!cfg.static && turretPivot) {
      // "world up" re-expressed in the pivot's current parent-local frame,
      // recomputed fresh every frame instead of once at build time - the
      // parent chain's orientation isn't static once driveMode is turning
      // the whole root, so a stale local axis would drift away from true
      // vertical and the "scan" would start reading as a downward dip
      // instead of a level side-to-side sweep.
      const parentWorldQuat = new THREE.Quaternion();
      turretPivot.parent.getWorldQuaternion(parentWorldQuat);
      const upLocal = new THREE.Vector3(0, 1, 0).applyQuaternion(parentWorldQuat.invert());
      turretYaw = Math.sin(t * turretScanSpeed) * turretScanAmplitude;
      turretPivot.quaternion.setFromAxisAngle(upLocal, turretYaw);
    }

    if (!cfg.static) {
      if (recoilPhase === 'idle') {
        nextShot -= dt;
        if (nextShot <= 0) { recoilPhase = 'kick'; recoil = 0; nextShot = 3.5 + Math.random() * 3.5; }
      } else if (recoilPhase === 'kick') {
        recoil = Math.min(recoil + dt * 13, 1);
        if (recoil >= 1) recoilPhase = 'return';
      } else if (recoilPhase === 'return') {
        recoil = Math.max(recoil - dt * 5, 0);
        if (recoil <= 0) recoilPhase = 'idle';
      }

      if (turretPivot && turretBase) {
        if (turretForwardLocal) {
          // straight backward along the turret's own forward direction (never
          // touches .y) - this is the "recoil" kick; it reads as the gun
          // sliding back into the hull, not dipping down.
          turretPivot.position.set(
            turretBase.x - turretForwardLocal.x * recoil * 0.07 * scale,
            turretBase.y,
            turretBase.z - turretForwardLocal.z * recoil * 0.07 * scale,
          );
        } else {
          turretPivot.position.copy(turretBase);
        }
      } else {
        // no separable turret to kick back - a small whole-body flinch instead
        const s = 1 - recoil * 0.03;
        chassis.scale.set(s, 1 + recoil * 0.02, s);
      }

      if (muzzleFlash) {
        // barrel telescopes in slightly along its own length on the kick,
        // easing back out through the return phase - reads as recoil absorbed
        // by the barrel itself, on top of the whole-turret kickback above.
        const squish = 1 - recoil * 0.1;
        muzzleFlash.gunMesh.scale.setComponent(
          'xyz'.indexOf(muzzleFlash.longAxis), squish,
        );
        const flashOn = recoilPhase === 'return' && recoil > 0.55;
        const flashT = flashOn ? (recoil - 0.55) / 0.45 : 0;
        muzzleFlash.cone.material.opacity = flashT * 0.9;
        muzzleFlash.cone.scale.setScalar(0.7 + recoil * 0.6);
        muzzleFlash.light.intensity = flashT * 3;
      }
    }

    eyeRig.update(dt, turretYaw, recoilPhase);
  }

  return {
    root,
    attachPoints: { turretTop: turretPivot || chassis, rear: chassis },
    skinMaterials,
    // Exposed mainly for tank-bake.js (public/tank-bake.js): lets a bake
    // harness hide the live pupils (only the white sclera gets baked into
    // the hull sprite - the pupil is drawn live on top at runtime, see
    // wild-percolating-breeze.md) and project the eye-mount world position
    // through its own bake camera without re-deriving eyeHeightFrac/
    // eyeSepFrac math here.
    eyeRig: { mounts: eyeRig.mounts, pupils: eyeRig.pupils },
    // set whenever buildAccessoryRig built the rig (cfg.accessory === 'debug',
    // or any mode once cfg.accessoryLayout is given) - the 4 groups
    // (flag1/flag2/smoke1/smoke2, each chassis-local) for tank-gallery.js's
    // accessory lab control panel to read/drive. See buildAccessoryRig.
    accessoryDebug: accessoryDebugRig,
    update: tick,
  };
}
